import gsap from "gsap";
import { useCallback, useLayoutEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/cn";
import { useNavEntrance } from "@/lib/useNavEntrance";

type NavItem = { to: string; label: string; icon: React.ComponentType<{ className?: string }> };

const REDUCED = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Bottom tab bar for mobile. A single pill sits behind the icons and slides
 * left/right to the active tab on every route change — GSAP animates its box
 * (`x`/`y`/`width`/`height`) to match the active icon's measured rect, so the
 * highlight tracks the navigation instead of just blinking on in place.
 *
 * Reduced motion, a viewport resize, and the first placement all jump straight
 * to position; only route-to-route moves animate.
 */
export function MobileTabBar({ nav }: { nav: readonly NavItem[] }) {
  const entranceRef = useNavEntrance<HTMLElement>({ axis: "y", distance: 8 });
  const navRef = useRef<HTMLElement | null>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const placed = useRef(false);
  const { pathname } = useLocation();

  // The nav node feeds both the entrance stagger and our own measurements.
  const setNavRef = useCallback(
    (node: HTMLElement | null) => {
      navRef.current = node;
      (entranceRef as React.MutableRefObject<HTMLElement | null>).current = node;
    },
    [entranceRef],
  );

  // Measure the active tab and move the pill to it. `animate` is ignored until
  // the pill has a real starting box to travel from.
  const place = useCallback((animate: boolean) => {
    const root = navRef.current;
    const pill = pillRef.current;
    const active = root?.querySelector<HTMLElement>("[data-tab-target][data-active]");
    if (!root || !pill || !active) return;

    const rootBox = root.getBoundingClientRect();
    const box = active.getBoundingClientRect();
    if (box.width === 0) return; // nav not laid out (e.g. desktop breakpoint)

    const next = {
      x: box.left - rootBox.left,
      y: box.top - rootBox.top,
      width: box.width,
      height: box.height,
      autoAlpha: 1,
    };
    const instant =
      !animate || !placed.current || REDUCED() || document.visibilityState !== "visible";
    placed.current = true;

    return instant
      ? void gsap.set(pill, next)
      : gsap.to(pill, { ...next, duration: 0.38, ease: "power3.out" });
  }, []);

  useLayoutEffect(() => {
    const tween = place(true);
    return () => {
      if (tween instanceof gsap.core.Tween) tween.kill();
    };
  }, [pathname, place]);

  useLayoutEffect(() => {
    const onResize = () => place(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [place]);

  return (
    <nav
      ref={setNavRef}
      className={cn(
        "fixed inset-x-0 bottom-0 z-20 flex items-stretch justify-around gap-1 border-t border-line",
        "bg-surface/90 px-3 pt-2 backdrop-blur-md lg:hidden",
        // Notch inset stacks on the base padding instead of replacing it.
        "pb-[calc(env(safe-area-inset-bottom,0px)+0.5rem)]",
      )}
    >
      {/* Sliding highlight — GSAP drives x/y/width/height off the active icon box,
          so it starts at the nav's origin and translates from there. */}
      <span
        ref={pillRef}
        aria-hidden
        className="pointer-events-none absolute left-0 top-0 z-0 h-9 rounded-pill bg-accent opacity-0"
      />

      {nav.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          data-nav-item
          className={({ isActive }) =>
            cn(
              "relative z-10 flex flex-1 flex-col items-center gap-1.5 rounded-2xl py-1.5 text-[0.6875rem] font-medium transition-colors",
              isActive ? "text-ink" : "text-ink-faint",
            )
          }
        >
          {({ isActive }) => (
            <>
              <span
                data-tab-target
                {...(isActive ? { "data-active": "" } : {})}
                className={cn(
                  "grid h-9 w-16 place-items-center rounded-pill transition-colors",
                  isActive && "text-accent-ink",
                )}
              >
                <Icon className="h-[1.15rem] w-[1.15rem]" />
              </span>
              {label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
